import React, { useEffect, useState, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { XIcon, ClockIcon, TrendingUpIcon, ChevronRightIcon } from 'lucide-react';
import MobileTopBar from './MobileTopBar';
import MobileBottomBar from './MobileBottomBar';
import MobileMenu from './MobileMenu';
import { SearchWithAutocomplete, Suggestion } from '../UI/SearchWithAutocomplete';
import { useDarkClasses } from '../../hooks/useDarkClasses';
// Mock data for search suggestions
const mockSearchSuggestions: Suggestion[] = [{
  id: 'b1',
  label: 'The Silent Echo',
  sublabel: 'by Elena Richards',
  type: 'Book',
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=100'
}, {
  id: 'b2',
  label: 'Beyond the Veil',
  sublabel: 'by Marcus Chen',
  type: 'Book',
  image: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?w=100'
}, {
  id: 'a1',
  label: 'Elena Richards',
  sublabel: 'Author',
  type: 'Author',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'
}, {
  id: 'a2',
  label: 'Marcus Chen',
  sublabel: 'Author',
  type: 'Author',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100'
}, {
  id: 'g1',
  label: 'Romance',
  type: 'Genre'
}, {
  id: 'g2',
  label: 'Fantasy',
  type: 'Genre'
}, {
  id: 'g3',
  label: 'Mystery',
  type: 'Genre'
}, {
  id: 'm1',
  label: 'Silent Echo T-Shirt',
  sublabel: 'Apparel',
  type: 'Merch',
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=100'
}];
// Mock trending searches
const trendingSearches = ['Enemies to Lovers', 'Fantasy Romance', 'Book Boxes', 'Sarah J. Maas', 'Special Editions'];
interface MobileNavigationProps {
  isReader: boolean;
  toggleUserRole: () => void;
  cartItemCount: number;
}
const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isReader,
  toggleUserRole,
  cartItemCount
}) => {
  const dk = useDarkClasses();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showTopBar, setShowTopBar] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse recent searches', e);
      }
    }
  }, []);
  const addToRecentSearches = (term: string) => {
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  };
  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };
  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);
  // Handle scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > 100) {
        if (currentScrollY > lastScrollY && showTopBar) {
          setShowTopBar(false);
        } else if (currentScrollY < lastScrollY && !showTopBar) {
          setShowTopBar(true);
        }
      } else {
        setShowTopBar(true);
      }
      setLastScrollY(currentScrollY);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY, showTopBar]);
  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);
  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen || isSearchOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isMenuOpen, isSearchOpen]);
  return <>
      <MobileTopBar isReader={isReader} showTopBar={showTopBar} isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} isSearchOpen={isSearchOpen} setIsSearchOpen={setIsSearchOpen} cartItemCount={cartItemCount} data-id="element-2734" data-mp="o4qty54skv6p" />
      <MobileBottomBar isReader={isReader} cartItemCount={cartItemCount} data-id="element-2735" data-mp="3rlqumkwd63kf" />
      <MobileMenu ref={menuRef} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} isReader={isReader} toggleUserRole={toggleUserRole} data-id="element-2736" data-mp="3nl6z1lrfv80e" />

      {/* Full-screen search overlay */}
      {isSearchOpen && <div className={`fixed inset-0 z-50 pt-safe animate-slide-up flex flex-col ${dk.isDark ? 'bg-gray-900' : 'bg-white'}`} data-id="element-2737" data-mp="1gt5xnvih3r2q">
          <div className={`flex items-center p-4 border-b ${dk.isDark ? 'border-gray-700' : 'border-gray-100'}`} data-id="element-2738" data-mp="1btta18js2fz3">
            <div className="flex-1 relative" data-id="element-2739" data-mp="1nwm78b6eykid">
              <SearchWithAutocomplete value={searchQuery} onChange={setSearchQuery} suggestions={mockSearchSuggestions} placeholder="Search books, authors, merch..." mobileFullscreen={true} autoFocus={true} showIcon={true} onSelect={suggestion => {
            setSearchQuery(suggestion.label);
            addToRecentSearches(suggestion.label);
          }} onSearch={query => {
            addToRecentSearches(query);
          }} data-id="element-2740" data-mp="1nvb32a923aqq" />
            </div>
            <button onClick={() => setIsSearchOpen(false)} className={`ml-3 font-medium p-2 active:opacity-70 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-2741" data-mp="1exr10z3ddaf2">
              Cancel
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pb-safe" data-id="element-2742" data-mp="euszry5vvwd0">
            {!searchQuery && <div className="p-4 space-y-8" data-id="element-2743" data-mp="9y2g70uveimu">
                {/* Recent Searches */}
                {recentSearches.length > 0 && <div data-id="element-2744" data-mp="50108osqqzfl">
                    <div className="flex items-center justify-between mb-3" data-id="element-2745" data-mp="3w4hknitiiu8n">
                      <h3 className={`text-xs font-bold uppercase tracking-wider ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2746" data-mp="17lkt1px2du">
                        Recent Searches
                      </h3>
                      <button onClick={clearRecentSearches} className={`text-xs font-medium ${dk.isDark ? 'text-indigo-400' : 'text-indigo-600'}`} data-id="element-2747" data-mp="3ic1azz63vxyr">
                        Clear
                      </button>
                    </div>
                    <ul className="space-y-1" data-id="element-2748" data-mp="34e4gr9f4k3n9">
                      {recentSearches.map((term, idx) => <li key={idx} data-id="element-2749" data-mp="w5og63mzdrso" data-mpi={idx}>
                          <button onClick={() => setSearchQuery(term)} className={`flex items-center w-full py-3 ${dk.isDark ? 'text-gray-200 active:bg-gray-800' : 'text-gray-700 active:bg-gray-50'}`} data-id="element-2750" data-mp="w2iukydk981p">
                            <ClockIcon className={`h-4 w-4 mr-3 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2751" data-mp="r7d8nzk5rtdm" />
                            <span className="text-base" data-id="element-2752" data-mp="i5dncv24234v">{term}</span>
                          </button>
                        </li>)}
                    </ul>
                  </div>}

                {/* Trending Searches */}
                <div data-id="element-2753" data-mp="3qjvrwmsuf0jq">
                  <h3 className={`text-xs font-bold uppercase tracking-wider mb-3 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-2754" data-mp="3qlgmudj2kli4">
                    Trending Now
                  </h3>
                  <ul className="space-y-1" data-id="element-2755" data-mp="3bxypk07u6t5q">
                    {trendingSearches.map((term, idx) => <li key={idx} data-id="element-2756" data-mp="370otw2tutl9g" data-mpi={idx}>
                        <button onClick={() => setSearchQuery(term)} className={`flex items-center w-full py-3 ${dk.isDark ? 'text-gray-200 active:bg-gray-800' : 'text-gray-700 active:bg-gray-50'}`} data-id="element-2757" data-mp="3kywklt0arqc6">
                          <TrendingUpIcon className="h-4 w-4 text-indigo-500 mr-3" data-id="element-2758" data-mp="3pxhmdwv76i2q" />
                          <span className="text-base" data-id="element-2759" data-mp="3pvdgxahnhb0c">{term}</span>
                          <ChevronRightIcon className={`h-4 w-4 ml-auto ${dk.isDark ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-2760" data-mp="7i72x8d016te" />
                        </button>
                      </li>)}
                  </ul>
                </div>
              </div>}
          </div>
        </div>}
    </>;
};
export default MobileNavigation;